/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Box, Flex, HStack, Portal, Text, useCheckbox } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

// --------------------------------------------------
// Radio-like checkbox
// --------------------------------------------------
function RadioStyleCheckbox(props) {
  const { getInputProps, getCheckboxProps, state } = useCheckbox(props);

  return (
    <Box as="label" onClick={props.onClick}>
      <input {...getInputProps()} hidden />
      <Box
        {...getCheckboxProps()}
        cursor="pointer"
        borderWidth="1px"
        borderRadius="full"
        px={4}
        py={2}
        display="flex"
        alignItems="center"
        gap={2}
        transition="0.2s"
        _checked={{
          bg: "blue.500",
          color: "white",
          borderColor: "blue.600",
        }}
        _hover={{ borderColor: "blue.300" }}
      >
        <Box
          w="12px"
          h="12px"
          borderRadius="full"
          borderWidth="2px"
          borderColor="currentColor"
          bg={state.isChecked ? "white" : "transparent"}
        />
        <Text>{props.children}</Text>
      </Box>
    </Box>
  );
}

// --------------------------------------------------
// MAIN component
// --------------------------------------------------
export function RadioTypeMultiSelect({
  options,
  load,
  loading,
  value = "", // CSV string
  onChange,
  spacing = 3,
  disabled,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fallbackOptions, setFallbackOptions] = useState([]);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const normalizedValue = value ? value.split(",").map((v) => v.trim()) : [];

  useEffect(() => {
    if (!normalizedValue.length) return;

    const missing = normalizedValue
      .filter((v) => !options.some((o) => o.value === v))
      .map((v) => ({ value: v, label: v }));

    setFallbackOptions(missing);
  }, [normalizedValue, options]);

  const mergedOptions = [...options, ...fallbackOptions];

  const toggle = (val) => {
    const exists = normalizedValue.includes(val);
    const updated = exists
      ? normalizedValue.filter((v) => v !== val)
      : [...normalizedValue, val];

    onChange(updated.join(",")); // <-- send CSV
  };

  const handleToggle = () => {
    if (!isExpanded) {
      load();
    }
    setIsExpanded(!isExpanded);
  };

  // Close when clicking outside
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isExpanded]);

  // Get selected options for collapsed view
  const selectedOptions = mergedOptions.filter((opt) =>
    normalizedValue.includes(opt.value)
  );

  return (
    <Box position="relative">
      {/* Collapsed view - shows selected values as pills */}
      <Flex
        ref={triggerRef}
        borderWidth="1px"
        borderRadius="md"
        p={3}
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled ? 0.6 : 1}
        onClick={disabled ? undefined : handleToggle}
        justifyContent="space-between"
        alignItems="center"
        bg={isExpanded ? "gray.50" : "white"}
        _hover={disabled ? {} : { bg: "gray.50" }}
        minH="48px"
      >
        <HStack flex={1} spacing={2} overflow="hidden">
          {selectedOptions.length > 0 ? (
            <>
              {selectedOptions.slice(0, 3).map((opt) => (
                <Box
                  key={opt.value}
                  px={3}
                  py={1}
                  bg="blue.500"
                  color="white"
                  borderRadius="full"
                  fontSize="sm"
                  whiteSpace="nowrap"
                >
                  {opt.label}
                </Box>
              ))}
              {selectedOptions.length > 2 && (
                <Box
                  px={3}
                  py={1}
                  bg="gray.500"
                  color="white"
                  borderRadius="full"
                  fontSize="sm"
                >
                  +{selectedOptions.length - 2}
                </Box>
              )}
            </>
          ) : (
            <Text fontSize="sm" color="gray.500">
              Select options...
            </Text>
          )}
        </HStack>
        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Flex>

      {/* Expanded view - large centered popup */}
      {isExpanded && (
        <Portal>
          {/* Backdrop overlay */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.300"
            zIndex={9998}
            onClick={() => setIsExpanded(false)}
          />

          {/* Centered popup */}
          <Box
            ref={dropdownRef}
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="600px"
            maxWidth="90vw"
            maxH="70vh"
            overflowY="auto"
            borderWidth="1px"
            borderRadius="lg"
            p={6}
            bg="white"
            boxShadow="2xl"
            zIndex={9999}
          >
            <Text fontSize="lg" fontWeight="semibold" mb={4}>
              Select Options
            </Text>
            <HStack spacing={spacing} wrap="wrap">
              {loading ? (
                <Box fontSize="sm" color="gray.500">
                  Loading...
                </Box>
              ) : (
                mergedOptions.map((opt) => (
                  <RadioStyleCheckbox
                    key={opt.value}
                    isChecked={normalizedValue.includes(opt.value)}
                    onClick={() => !disabled && toggle(opt.value)}
                  >
                    {opt.label}
                  </RadioStyleCheckbox>
                ))
              )}
            </HStack>
          </Box>
        </Portal>
      )}
    </Box>
  );
}
